import { Component, OnInit, DoCheck, ViewChildren, QueryList, ElementRef, ChangeDetectorRef, OnDestroy } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { Db3DApiBackendClient, QueryParamsForGetUsers, UserDetailPayload } from "src/app/services/api/db3d-api-backend-client.service";
import { environment } from "src/environments/environment";
import { UsersListService } from "src/app/services/users/users-list.service";
import { NavigationService } from "src/app/services/NavigationService.service";
import { WhiteLabelConfigurationService } from "src/app/services/white-labels/white-label-configuration.service";
import { queryParamsKey, TableHeaderParam } from "src/app/services/users/users-list.constants";
import { UsersListBase } from "src/app/components/users-list/users-list.base";
import { UsersTableComponent } from "src/app/components/users-list/users-table/users-table.component";
import { HttpErrorResponse } from "@angular/common/http";
import { UserDetails } from "src/app/data-models/manage-users/user-details.model";
import { Subscription } from "rxjs";
import { IUserDetailsApiResponse } from "src/app/factories/user-details-factory";
import { ToastService, ToastType } from "src/app/services/toast.service";
import { UsersDataFetchMessages } from "src/app/data-models/manage-users/users-data-fetch-messages";

@Component({
  selector: "app-manage-price-factor",
  templateUrl: "./manage-price-factor.component.html",
  styleUrls: ["../users-list/users-list.component.scss"]
})

export class ManagePriceFactorComponent extends UsersListBase implements OnInit, DoCheck, OnDestroy {

  private static retailRole = "retail";
  private idTable = "priceFactorTable";
  public readonly iconPath = "assets/images/icons/arrow-down.png";
  public environment = environment;
  public readonly tableHeadersParameters: TableHeaderParam[] = [
    {
      name: "Email",
      orderingKey: "email",
    },
    {
      name: "Name",
      orderingKey: "full_name",
    },
    {
      name: "Price factor",
      orderingKey: "price_factor",
    },
  ];
  public wlSlug: string;
  public loadingMsg: string =  UsersDataFetchMessages.loading;
  public errorMsg: string = UsersDataFetchMessages.error;
  public emptyListMsg: string = UsersDataFetchMessages.emptyList;

  @ViewChildren("sortingArrow") protected arrows!: QueryList<ElementRef<HTMLElement>>;
  @ViewChildren(UsersTableComponent) protected usersTableComponents!: QueryList<UsersTableComponent>;

  private whiteLabelSubscription: Subscription;

  constructor(
    usersListService: UsersListService,
    protected router: Router,
    protected route: ActivatedRoute,
    public navigationService: NavigationService,
    private whiteLabelConfigurationService: WhiteLabelConfigurationService,
    private db3DApiBackendClient: Db3DApiBackendClient,
    private cdr: ChangeDetectorRef,
    public toastService: ToastService,
  ) {
    super(usersListService, router, route, navigationService);
  }

  ngOnInit(): void {
    this.whiteLabelSubscription = this.whiteLabelConfigurationService.whiteLabelSetupConfiguration.subscribe({
      next: (config) => {
        this.wlSlug = config.slug;
      },
    });

    const queryParamsFromUrl: QueryParamsForGetUsers = {
      page: this.route.snapshot.queryParamMap.get(queryParamsKey.page),
      search: this.route.snapshot.queryParamMap.get(queryParamsKey.search),
      ordering: this.route.snapshot.queryParamMap.get(queryParamsKey.sort),
      userRole: ManagePriceFactorComponent.retailRole,
    };

    this.initialize(queryParamsFromUrl);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [queryParamsKey.userRole]: ManagePriceFactorComponent.retailRole },
      queryParamsHandling: "merge",
      skipLocationChange: false,
    });
  }

  ngDoCheck() {
    if(this.route.snapshot.queryParamMap.get(queryParamsKey.sort)) this.addActiveStyleForSortingArrows(this.route.snapshot.queryParamMap.get(queryParamsKey.sort));
  }

  ngOnDestroy(): void {
    if (this.whiteLabelSubscription) this.whiteLabelSubscription.unsubscribe();
  }

  public isUsersListEmpty(): boolean {
    return !(this.users?.length > 0);
  }

  public changePage(pageNumber: number): void {
    super.changePage(pageNumber);
  }

  public addActiveStyleForSortingArrows(orderingQueryParams: string): void { 
    super.addActiveStyleForSortingArrows(orderingQueryParams);
  }
  
  public findSortingArrow(direction: string, sortBy: string): ElementRef<HTMLElement> | undefined { 
    return super.findSortingArrow(direction, sortBy);
  }
  
  public onPriceFactorEdited(event: { user: UserDetails; newValue: number }): void {
    const parsed = event.newValue;

    if (parsed === null || !Number.isFinite(parsed)) {
      const table = this.getFirstUsersTableComponent();
      if (table && event?.user) table.setInputValueForUser(event.user.id, event.user.priceFactor);
      return;
    }

    this.updatePriceFactor(event.user, parsed);
  }

  private getFirstUsersTableComponent(): UsersTableComponent | null {
    if (this.usersTableComponents && this.usersTableComponents.length > 0)
      return this.usersTableComponents.first || null;
    console.warn("No UsersTableComponent found.");
    return null;
  }

  private updatePriceFactor(user: UserDetails, newValue: number): Subscription {
    const payload: {[UserDetailPayload.PriceFactor]: number} = {
      [UserDetailPayload.PriceFactor]: newValue,
    };

    return this.db3DApiBackendClient.patchUsersPriceFactor(environment.db3dBackendDomain, String(user?.id), payload).subscribe({
      next: (res: IUserDetailsApiResponse) => {
        const table = this.getFirstUsersTableComponent();
        if (table) table.setInputValueForUser(user.id, res.price_factor);
        this.toastService.showToast("Changes saved!", ToastType.success);
      },
      error: (err: HttpErrorResponse) => {
        const table = this.getFirstUsersTableComponent();
        if (table) {
          table.setInputValueForUser(user.id, user.priceFactor);
          table.setLastValidPrice(user.id, user.priceFactor);
        }

        this.toastService.showToast("Changes unsaved!", ToastType.error);
        if (err?.status === 403) this.router.navigate([this.navigationService.pageNotFound]);
      }
    });
  }
}
